import type { StatusColorName } from "@kontextwerk/web-sdk/core/display/status"
import { mdiHomeOutline, mdiOfficeBuilding } from "@mdi/js"

import {
  DACH_COUNTRY_OPTIONS,
  formatAddressInline,
  normalizeCountryLabel,
} from "../../../../../_shared/_logic/countries"
import {
  findPropertyUnitById,
  formatPropertyUnitIdentifier,
  formatPropertyUnitLabel,
} from "../../../../../_shared/_logic/unit-labels"

type PropertyUnitLike = {
  id?: unknown
  unit_label?: unknown
  label?: unknown
  unit_number?: unknown
  unit_type?: unknown
}

type PropertyLike = {
  street?: unknown
  house_number?: unknown
  zip?: unknown
  city?: unknown
  country?: unknown
  address_extra?: unknown
  units?: PropertyUnitLike[] | null
}

export type PropertyObjectScope = "BUILDING" | "RESIDENTIAL"

export type PropertyObjectTagMode = "address-first" | "unit-first"

export type PropertyObjectTagPresentation = {
  label: string
  subLabel: string | null
  color: StatusColorName
  icon: string
  scope: PropertyObjectScope
  sortLabel: string
}

const normalizeText = (value: unknown) => `${value ?? ""}`.trim()
const normalizeUnitType = (unit: PropertyUnitLike | null | undefined) =>
  normalizeText(unit?.unit_type).toUpperCase()

export const propertyObjectColorForScope = (_scope: PropertyObjectScope): StatusColorName =>
  "neutral"

export const propertyObjectIconForScope = (scope: PropertyObjectScope) =>
  scope === "RESIDENTIAL" ? mdiHomeOutline : mdiOfficeBuilding

export const resolvePropertyObjectScope = (
  property?: PropertyLike | null,
  propertyUnitId?: string | null,
): PropertyObjectScope => {
  const unit = findPropertyUnitById(property, propertyUnitId)
  const unitType = normalizeUnitType(unit)

  if (unitType === "BUILDING") return "BUILDING"

  if (unitType === "RESIDENTIAL") return "RESIDENTIAL"

  return normalizeText(propertyUnitId) ? "RESIDENTIAL" : "BUILDING"
}

export const formatPropertyStreetHouseLine = (
  property?: PropertyLike | null,
  fallback?: string | null,
) => {
  const streetHouse = [property?.street, property?.house_number]
    .map(normalizeText)
    .filter(Boolean)
    .join(" ")

  if (streetHouse) return streetHouse

  const address = property ? formatAddressInline(property as Property) : ""

  return address || normalizeText(fallback) || "Unbekannte Liegenschaft"
}

export const formatPropertyObjectAddressLine = (
  property?: PropertyLike | null,
  fallback?: string | null,
) => {
  const streetHouse = [property?.street, property?.house_number]
    .map(normalizeText)
    .filter(Boolean)
    .join(" ")
  const address = [streetHouse, normalizeText(property?.city)].filter(Boolean).join(", ")

  if (address) return address

  const parts = normalizeText(fallback).split(",").map(normalizeText).filter(Boolean)
  const postalCity = parts.find((part, index) => index > 0 && /^\d{4,5}\s+/.test(part))

  if (postalCity) return [parts[0], postalCity.replace(/^\d{4,5}\s+/, "")].join(", ")

  const lastPart = parts.at(-1)

  if (
    parts.length > 2 &&
    DACH_COUNTRY_OPTIONS.some((country) => country.value === normalizeCountryLabel(lastPart, ""))
  ) {
    parts.pop()
  }

  return (
    parts
      .map((part, index) =>
        index > 0 || parts.length === 1 ? part.replace(/^\d{4,5}\s+/, "") : part,
      )
      .filter(Boolean)
      .join(", ") || "Unbekannte Liegenschaft"
  )
}

const formatPropertyObjectUnitLine = ({
  property,
  propertyUnitId,
  fallbackUnitLabel,
}: {
  property?: PropertyLike | null
  propertyUnitId?: string | null
  fallbackUnitLabel?: string | null
}) => {
  const unit = findPropertyUnitById(property, propertyUnitId)
  const unitLabel = unit ? formatPropertyUnitIdentifier(unit) : ""

  if (unitLabel) return unitLabel

  const fallback = normalizeText(fallbackUnitLabel)

  if (fallback) return formatPropertyUnitLabel(fallback)

  const normalizedUnitId = normalizeText(propertyUnitId)

  if (normalizedUnitId) return "Wohneinheit"

  return "Gebäudeeinheit"
}

export const buildPropertyObjectTagPresentation = ({
  property,
  propertyUnitId,
  fallbackAddressLabel,
  fallbackUnitLabel,
  mode = "address-first",
}: {
  property?: PropertyLike | null
  propertyUnitId?: string | null
  fallbackAddressLabel?: string | null
  fallbackUnitLabel?: string | null
  mode?: PropertyObjectTagMode
}): PropertyObjectTagPresentation => {
  const scope = resolvePropertyObjectScope(property, propertyUnitId)
  const addressLabel =
    mode === "address-first"
      ? formatPropertyObjectAddressLine(property, fallbackAddressLabel)
      : formatPropertyStreetHouseLine(property, fallbackAddressLabel)
  const unitLabel = formatPropertyObjectUnitLine({
    property,
    propertyUnitId,
    fallbackUnitLabel,
  })
  const label = mode === "unit-first" ? unitLabel : addressLabel
  const subLabel = mode === "unit-first" ? addressLabel : unitLabel
  const sortLabel = [label, subLabel].filter(Boolean).join(" ")

  return {
    label: label || "Unbekannte Einheit",
    subLabel: subLabel || null,
    color: propertyObjectColorForScope(scope),
    icon: propertyObjectIconForScope(scope),
    scope,
    sortLabel,
  }
}
